import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import type { MouseEvent, ReactNode } from 'react';
import { useMemo } from 'react';
import strings from '../../../common/Translation/Translate';
import { getBookingStatusChipSx } from '../../../utils/bookingStatusChipStyles';
import { getPricePackageName } from '../../../utils/pricePackage';
import { calculateExpectedGuests } from '../../../utils/validators/HelperFunctions';

type DropdownOption = { label: string; value: string | number };

type SortedLineItemRow = { item: any; originalIndex: number };

interface LineItemsTableComponentProps {
  lineItems: any;
  setLineItems: any;
  gridError: any;
  venueList: DropdownOption[];
  statusList: DropdownOption[];
  partOfDayList: DropdownOption[];
  setSelectedRowId: any;
  setSelectedVenue: any;
  setLineItemsPageResponse: any;
  setBookedAddOns: any;
  setIsEditDrawer: any;
  setIsCloneDrawer: any;
  setOpenDrawer: (open: boolean) => void;
  onCloneRow: (idx: number) => void;
  canOpenLineItemDrawer?: () => boolean;
  selectedEventType: any;
}

const GRID_COLUMNS = {
  xs: '1fr',
  sm: 'minmax(0, 1.25fr) minmax(0, 1.05fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(5.5rem, max-content) 96px',
};


const formatEventSubtype = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value !== null) {
    const obj = value as { label?: string; value?: string };
    return (obj.label || obj.value || '').trim();
  }
  return String(value).trim();
};

const VenueCell = ({ venueName, statusChip }: { venueName: string; statusChip: ReactNode }) => (
  <Stack spacing={0.35}>
    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.4 }}>
      {venueName}
    </Typography>
    {statusChip}
  </Stack>
);

const EventTypePackageCell = ({
  eventSubtype,
  packageLabel,
}: {
  eventSubtype: string;
  packageLabel: string;
}) => (
  <Stack spacing={0.15}>
    {eventSubtype ? (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {eventSubtype}
      </Typography>
    ) : null}
    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
      {packageLabel}
    </Typography>
  </Stack>
);

const DateTimeCell = ({
  dateLine,
  timeLine,
  partOfDayLabel,
}: {
  dateLine: string;
  timeLine: string;
  partOfDayLabel: string;
}) => (
  <Stack spacing={0.15}>
    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
      {dateLine}
    </Typography>
    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
      {timeLine}
    </Typography>
    {partOfDayLabel && partOfDayLabel !== '—' ? (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {partOfDayLabel}
      </Typography>
    ) : null}
  </Stack>
);

const GuestsCell = ({ guaranteed, expectedLine }: { guaranteed: string; expectedLine: string }) => (
  <Stack spacing={0.15}>
    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.4 }}>
      {guaranteed}
    </Typography>
    {expectedLine ? (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {expectedLine}
      </Typography>
    ) : null}
  </Stack>
);

const formatPricePackageLabel = (item: any): string => {
  const label = getPricePackageName(item, '');
  if (label) return label;
  if (item.pricePackage == null && (item.pricePerHour != null || item.totalRental != null)) {
    return strings.hourlyPricing1;
  }
  return '—';
};

const resolveDropdownLabel = (value: unknown, options: DropdownOption[]): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value !== null && 'label' in (value as object)) {
    return String((value as { label?: string }).label ?? '').trim();
  }
  const resolved =
    typeof value === 'object' && value !== null && 'value' in (value as object)
      ? (value as { value: string | number }).value
      : value;
  return options.find(o => String(o.value) === String(resolved))?.label?.trim() ?? String(resolved);
};

const formatLineItemDate = (startRaw: unknown, endRaw: unknown): string => {
  const start = startRaw ? dayjs(startRaw as string) : null;
  const end = endRaw ? dayjs(endRaw as string) : null;
  if (!start?.isValid() && !end?.isValid()) return '—';
  if (start?.isValid() && end?.isValid() && !start.isSame(end, 'day')) {
    if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
      return `${start.format('D')}–${end.format('D MMM YYYY')}`;
    }
    return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
  }
  return (start?.isValid() ? start : end!)!.format('D MMM YYYY');
};

const formatDisplayTime = (timeRaw: unknown): string => {
  if (timeRaw == null || timeRaw === '') return '';
  const time = String(timeRaw).trim();
  const parsed = dayjs(time, ['HH:mm:ss', 'HH:mm', 'H:mm', 'h:mm A'], true);
  if (parsed.isValid()) return parsed.format('h:mm A');
  return time;
};

const formatTimeRange = (startTime: unknown, endTime: unknown): string => {
  const start = formatDisplayTime(startTime);
  const end = formatDisplayTime(endTime);
  if (start && end) return `${start} – ${end}`;
  return start || end || '—';
};

const isRowInvalid = (item: any, gridError: boolean): boolean =>
  !!gridError &&
  (item.status === '' ||
    item.status === undefined ||
    item.pricePackage === '' ||
    !item.startDate ||
    String(item.startDate).trim() === '');

const getLineItemSortKey = (item: any): number => {
  const date = item.startDate ? dayjs(item.startDate as string) : null;
  if (!date?.isValid()) return Number.MAX_SAFE_INTEGER;

  let startMinutes = 0;
  if (item.startTime != null && item.startTime !== '') {
    const time = dayjs(String(item.startTime), ['HH:mm:ss', 'HH:mm', 'H:mm', 'h:mm A'], true);
    if (time.isValid()) {
      startMinutes = time.hour() * 60 + time.minute();
    }
  }

  return date.startOf('day').valueOf() + startMinutes;
};

const statusChipSx = (status: string) => getBookingStatusChipSx(status);

const compactStatusChipSx = {
  fontWeight: 600,
  fontSize: '0.65rem',
  height: 20,
  alignSelf: 'flex-start',
  '& .MuiChip-label': { px: 0.75, py: 0, lineHeight: 1.2 },
};

const LineItemsTableComponent = ({
  lineItems,
  setLineItems,
  gridError,
  venueList,
  statusList,
  partOfDayList,
  setSelectedRowId,
  setSelectedVenue,
  setLineItemsPageResponse,
  setBookedAddOns,
  setIsEditDrawer,
  setIsCloneDrawer,
  setOpenDrawer,
  onCloneRow,
  canOpenLineItemDrawer,
}: LineItemsTableComponentProps) => {
  const theme = useTheme();

  const sortedLineItems = useMemo((): SortedLineItemRow[] => {
    const rows = (lineItems ?? []).map(
      (item: any, originalIndex: number): SortedLineItemRow => ({ item, originalIndex }),
    );
    return rows.sort(
      (a: SortedLineItemRow, b: SortedLineItemRow) =>
        getLineItemSortKey(a.item) - getLineItemSortKey(b.item),
    );
  }, [lineItems]);

  const deleteRow = (idx: number, event: MouseEvent) => {
    event.stopPropagation();
    const deleteRowCopy = JSON.parse(JSON.stringify(lineItems));
    setLineItems(deleteRowCopy.filter((_: any, i: number) => i !== idx));
  };

  const editRow = (idx: number) => {
    if (canOpenLineItemDrawer && !canOpenLineItemDrawer()) return;
    setIsCloneDrawer(false);
    setSelectedRowId(idx);
    const editRowCopy = JSON.parse(JSON.stringify(lineItems));
    const editValue = editRowCopy.find((_: any, i: number) => i === idx);
    const a = {
      ...editValue,
      startTime: dayjs(editValue.startTime, 'HH:mm'),
      endTime: dayjs(editValue.endTime, 'HH:mm'),
      totalRental:
        dayjs(editValue.endTime, 'HH:mm').diff(dayjs(editValue.startTime, 'HH:mm'), 'hours', true) *
        editValue?.pricePerHour,
    };
    setLineItemsPageResponse(a);
    setSelectedVenue(editValue.venue as string);
    setBookedAddOns(editValue.addOns);
    setIsEditDrawer(true);
    setOpenDrawer(true);
  };

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: 'start',
    columnGap: 2,
  };

  const headerLabels: { label: string; align?: 'right' }[] = [
    { label: strings.new_booking_venue },
    { label: strings.new_booking_column_date_time },
    { label: strings.new_booking_column_guests },
    { label: strings.new_booking_column_event_type_package },
    { label: strings.new_booking_column_total, align: 'right' },
    { label: strings.customermgmt_column_action, align: 'right' },
  ];

  const renderActions = (idx: number): ReactNode => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      <Tooltip title={strings.edit_bookings_multiple}>
        <IconButton
          size="small"
          color="primary"
          aria-label={strings.edit_bookings_multiple}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            editRow(idx);
          }}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={strings.BookingDetailButonTextClone}>
        <IconButton
          size="small"
          color="primary"
          aria-label={strings.BookingDetailButonTextClone}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onCloneRow(idx);
          }}>
          <ContentCopyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={strings.deleteText}>
        <IconButton
          size="small"
          color="error"
          aria-label={strings.deleteText}
          onClick={(e: MouseEvent) => deleteRow(idx, e)}>
          <DeleteOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  if (!lineItems?.length) return null;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box
        sx={{
          ...gridSx,
          display: { xs: 'none', sm: 'grid' },
          px: 2,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
        }}>
        {headerLabels.map(({ label, align }) => (
          <Typography
            key={label}
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            textAlign={align ?? 'left'}
            sx={{ letterSpacing: 0.2 }}>
            {label}
          </Typography>
        ))}
      </Box>

      {sortedLineItems.map(({ item, originalIndex }) => {
        const idx = originalIndex;
        const venueName = resolveDropdownLabel(item.venue, venueList) || '—';
        const eventSubtype = formatEventSubtype(item.eventSubtype);
        const dateLine = formatLineItemDate(item.startDate, item.endDate);
        const timeLine = formatTimeRange(item.startTime, item.endTime);
        const partOfDayLabel = resolveDropdownLabel(item.partOfDay, partOfDayList) || '—';
        const guaranteedGuests =
          item.guests != null && item.guests !== '' ? String(item.guests) : '—';
        const expectedGuests = calculateExpectedGuests(item.guests, item.excessGuestsPercentage);
        const expectedLine =
          expectedGuests > 0 && Number(item.excessGuestsPercentage) > 0
            ? `${strings.sa_booking_lookup_li_expected_abbr} ${expectedGuests}`
            : '';
        const packageLabel = formatPricePackageLabel(item);
        const statusValue =
          typeof item.status === 'object' && item.status !== null && 'value' in item.status
            ? (item.status as { value: string }).value
            : item.status;
        const statusLabel = resolveDropdownLabel(item.status, statusList) || statusValue || '—';
        const invalid = isRowInvalid(item, gridError);
        const totalLabel =
          item.lineItemTotal != null && String(item.lineItemTotal).trim() !== ''
            ? String(item.lineItemTotal)
            : '—';

        const statusChip =
          statusValue ? (
            <Chip
              label={statusLabel}
              size="small"
              variant="outlined"
              sx={{
                ...compactStatusChipSx,
                ...statusChipSx(String(statusValue)),
              }}
            />
          ) : (
            <Chip
              label="—"
              size="small"
              variant="outlined"
              color="error"
              sx={compactStatusChipSx}
            />
          );

        return (
          <Box
            key={`${idx}-${venueName}-${item.startDate ?? ''}`}
            onClick={() => editRow(idx)}
            sx={{
              px: 2,
              py: 1,
              cursor: 'pointer',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
              borderLeft: invalid ? `3px solid ${theme.palette.error.main}` : '3px solid transparent',
              bgcolor: invalid ? alpha(theme.palette.error.main, 0.04) : 'transparent',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
              '&:last-of-type': { borderBottom: 'none' },
            }}>
            <Box sx={{ ...gridSx, display: { xs: 'none', sm: 'grid' } }}>
              <VenueCell venueName={venueName} statusChip={statusChip} />
              <DateTimeCell dateLine={dateLine} timeLine={timeLine} partOfDayLabel={partOfDayLabel} />
              <GuestsCell guaranteed={guaranteedGuests} expectedLine={expectedLine} />
              <EventTypePackageCell eventSubtype={eventSubtype} packageLabel={packageLabel} />
              <Typography variant="body2" fontWeight={600} textAlign="right" sx={{ lineHeight: 1.4 }}>
                {totalLabel}
              </Typography>
              {renderActions(idx)}
            </Box>

            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                    {strings.new_booking_venue}
                  </Typography>
                  <VenueCell venueName={venueName} statusChip={statusChip} />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                    {strings.new_booking_column_date_time}
                  </Typography>
                  <DateTimeCell dateLine={dateLine} timeLine={timeLine} partOfDayLabel={partOfDayLabel} />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                    {strings.new_booking_column_guests}
                  </Typography>
                  <GuestsCell guaranteed={guaranteedGuests} expectedLine={expectedLine} />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                    {strings.new_booking_column_event_type_package}
                  </Typography>
                  <EventTypePackageCell eventSubtype={eventSubtype} packageLabel={packageLabel} />
                </Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                      {strings.new_booking_column_total}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {totalLabel}
                    </Typography>
                  </Box>
                  {renderActions(idx)}
                </Stack>
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LineItemsTableComponent;
